// Static content for the coach help guides, ported from the ll-ux-sandpit
// prototype (coach-first-time-dashboard). Presentation-only for now. Reuses the
// section types from the coachee guide since the shape is identical.

import type {
  QuickStartGuideSection,
  QuickStartGuideSupportContact,
} from '@efdigital/ll-xceed-v2/data/coaching/coachee/quick-start-guide';

export const COACH_HOW_COACHING_WORKS: QuickStartGuideSection = {
  id: 'how-coaching-works',
  title: 'How coaching works here',
  icon: 'ph:lightbulb',
  layout: 'steps',
  blocks: [
    {
      step: 1,
      title: 'Complete your profile',
      body: 'Add your photo, bio, specialties, credentials, and coaching approach. A complete profile helps coachees understand your style and improves your match quality.',
    },
    {
      step: 2,
      title: 'Set your availability',
      body: 'Choose when you are open for chemistry calls and coaching sessions. Keep your calendar up to date so coachees can book at times that work for both of you.',
    },
    {
      step: 3,
      title: 'Get matched with coachees',
      body: 'Once your profile is reviewed and approved, you become visible to coachees in your matched programs. Engagements are created when a coachee selects you.',
    },
    {
      step: 4,
      title: 'Meet for a chemistry call',
      body: 'Coachees may book a short introductory session to discuss goals and see if you are the right fit. There is no obligation for either side to continue after the call.',
    },
    {
      step: 5,
      title: 'Deliver coaching sessions',
      body: 'Run one-to-one sessions by video call. Join from the calendar invite when it is time. Use the Sessions tab to review upcoming bookings and flag any issues that need admin attention.',
    },
    {
      step: 6,
      title: 'Share notes and resources',
      body: 'Follow up after sessions with notes, articles, and files from your resource library. Shared materials help coachees take action between sessions.',
    },
    {
      step: 7,
      title: 'Track your delivered sessions',
      body: 'Delivered session credits appear on your dashboard so you can keep track of your coaching activity across the program.',
    },
  ],
};

export const COACH_WORKING_WITH_COACHEES: QuickStartGuideSection = {
  id: 'working-with-coachees',
  title: 'Working with coachees',
  icon: 'ph:handshake',
  layout: 'cards',
  intro:
    'Strong engagements start with clear expectations and consistent follow-through. Use these tips as you begin working with new coachees.',
  blocks: [
    {
      icon: 'ph:identification-card',
      title: 'Review the engagement before the first call',
      body: 'Read the coachee profile, program context, and stated goals before your chemistry call or first session. Note timezone, language, and any areas they want to focus on.',
    },
    {
      icon: 'ph:phone-call',
      title: 'Use chemistry calls to set the tone',
      body: 'Intro calls are a chance to explain your approach, ask about their priorities, and confirm whether you are a good match. Be clear about what happens next if they want to continue.',
    },
    {
      icon: 'ph:notebook',
      title: 'Share follow-up after each session',
      body: 'Send brief session notes or resources while the conversation is still fresh. This reinforces agreed actions and shows coachees you are engaged between sessions.',
    },
    {
      icon: 'ph:calendar',
      title: 'Respect scheduling rules',
      body: 'Encourage coachees to book and reschedule within program windows. If a session cannot go ahead, flag it promptly so credits and admin records stay accurate.',
    },
    {
      icon: 'ph:warning',
      title: 'Raise issues early',
      body: 'If a coachee misses a session, disengages, or raises a concern, use the platform flagging tools. Early visibility helps program administrators support both parties.',
    },
  ],
};

export const COACH_PLATFORM_HANDBOOK: QuickStartGuideSection = {
  id: 'platform-handbook',
  title: 'Platform handbook',
  icon: 'ph:book-open',
  layout: 'cards',
  intro:
    'Key details about engagements, session credits, and sharing materials with coachees on the coach platform.',
  blocks: [
    {
      title: 'Engagements overview',
      items: [
        'An engagement begins when a coachee selects you for a program.',
        'Chemistry calls and coaching sessions are tracked separately.',
        'Session history, notes, and shared resources live under each engagement.',
        'Pending flags and session issues appear on your dashboard for review.',
      ],
      footer:
        'Complete your profile and availability before expecting your first match.',
    },
    {
      title: 'Session credits',
      items: [
        'Coaching sessions are deducted from the coachee program balance when delivered.',
        'Chemistry calls use separate credits and do not reduce session totals.',
        'Flag missed or incomplete sessions promptly with supporting notes.',
        'Your dashboard shows session credits delivered across the program.',
      ],
    },
    {
      title: 'Share notes and resources',
      items: [
        'Write session notes after each call to capture themes and agreed actions.',
        'Share articles, templates, and files with coachees from your resource library.',
        'Upload reusable materials to build your library.',
        'Send follow-up soon after a session while the conversation is still fresh.',
      ],
    },
  ],
};

export const COACH_SUPPORT_ITEMS: QuickStartGuideSupportContact[] = [
  {
    icon: 'ph:envelope-simple',
    label: 'Coach network administrator',
    detail:
      'Questions about your profile review, matching, or platform access.',
    email: 'v-support@hultashridge.com',
  },
  {
    icon: 'ph:warning-circle',
    label: 'Urgent session issues',
    detail:
      'Message your coachee directly or contact support before the session starts.',
  },
];

// All coach guide sections in reading order, for the single-page accordion.
export const COACH_QUICK_START_GUIDE_SECTIONS: QuickStartGuideSection[] = [
  COACH_HOW_COACHING_WORKS,
  COACH_WORKING_WITH_COACHEES,
  COACH_PLATFORM_HANDBOOK,
];
